import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import type {FilterOptionItem} from '../hooks/useFilterOptions';

interface Props {
	label: string;
	options: FilterOptionItem[];
	selected: string[];
	onChange: (ids: string[]) => void;
}

const Section = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding-bottom: 12px;
`;

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
`;

const SectionLabel = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #334155;
`;

const CountBadge = styled.span`
	margin-left: 6px;
	display: inline-flex;
	height: 20px;
	min-width: 20px;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	background: #e0e7ff;
	padding: 0 6px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	color: #4338ca;
`;

const SearchInput = styled.input`
	width: 100%;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	padding: 6px 10px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #334155;
	&::placeholder {
		color: #94a3b8;
	}
	&:focus {
		outline: none;
		border-color: #a5b4fc;
		box-shadow: 0 0 0 1px #a5b4fc;
	}
`;

const OptionList = styled.ul`
	margin: 8px 0 0;
	padding: 0;
	max-height: 160px;
	overflow-y: auto;
	list-style: none;
	> li + li {
		margin-top: 2px;
	}
`;

const GroupLabel = styled.li`
	padding: 6px 4px 2px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #94a3b8;
`;

const OptionLabel = styled.label`
	display: flex;
	cursor: pointer;
	align-items: center;
	gap: 8px;
	border-radius: 4px;
	padding: 4px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #334155;
	&:hover {
		background: #f8fafc;
	}
`;

const Checkbox = styled.input`
	height: 14px;
	width: 14px;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	accent-color: #4f46e5;
`;

const ColorDot = styled.span`
	display: inline-block;
	height: 10px;
	width: 10px;
	flex-shrink: 0;
	border-radius: 9999px;
`;

const OptionText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const EmptyItem = styled.li`
	padding: 8px 4px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #94a3b8;
`;

export function FilterDrawerSearchableSelect({label, options, selected, onChange}: Props) {
	const [search, setSearch] = useState('');

	const filtered = useMemo(() => {
		if (!search.trim()) return options;
		const term = search.toLowerCase();
		return options.filter(o => o.label.toLowerCase().includes(term));
	}, [options, search]);

	const grouped = useMemo(() => {
		const groups = new Map<string, FilterOptionItem[]>();
		for (const opt of filtered) {
			const key = opt.group ?? '';
			const arr = groups.get(key) ?? [];
			arr.push(opt);
			groups.set(key, arr);
		}
		return groups;
	}, [filtered]);

	function toggle(id: string) {
		if (selected.includes(id)) {
			onChange(selected.filter(v => v !== id));
		} else {
			onChange([...selected, id]);
		}
	}

	const count = selected.length;

	return (
		<Section>
			<Head>
				<SectionLabel>
					{label}
					{count > 0 && <CountBadge>{count}</CountBadge>}
				</SectionLabel>
			</Head>
			<SearchInput
				type="text"
				placeholder={`Search ${label.toLowerCase()}...`}
				value={search}
				onChange={e => setSearch(e.target.value)}
			/>
			<OptionList>
				{Array.from(grouped.entries()).map(([group, items]) => (
					<React.Fragment key={group}>
						{group && <GroupLabel>{group}</GroupLabel>}
						{items.map(opt => {
							const checked = selected.includes(opt.id);
							return (
								<li key={opt.id}>
									<OptionLabel>
										<Checkbox type="checkbox" checked={checked} onChange={() => toggle(opt.id)} />
										{opt.color && <ColorDot style={{backgroundColor: opt.color}} />}
										<OptionText>{opt.label}</OptionText>
									</OptionLabel>
								</li>
							);
						})}
					</React.Fragment>
				))}
				{filtered.length === 0 && <EmptyItem>No results</EmptyItem>}
			</OptionList>
		</Section>
	);
}
